/**
 * useStrategyStats Hook
 * Shared between web and mobile for fetching strategy and prediction stats
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, type HttpClient } from '../api/httpClient';

// =============================================================================
// Types
// =============================================================================

export interface Stats {
  won: number;
  lost: number;
  profit: number;
}

export interface PlayerStats extends Stats {
  player: string;
  hitRate: number;
  totalBets: number;
  /** Average price of the legs in totalBets, over decimal odds; null when none has a price */
  avgOdds: number | null;
  byPropType: Record<string, Stats>;
  byDirection: Record<string, Stats>;
  avgMargin?: number;
  marginStdDev?: number;
  crushRate?: number;
  tightWinRate?: number;
}

export interface PropTypeStats extends Stats {
  propType: string;
  hitRate: number;
  byDirection: Record<string, Stats>;
}

export interface PlayerPredictionStats extends Stats {
  player: string;
  hitRate: number;
  totalPicks: number;
  byPropType: Record<string, Stats>;
  byDirection: Record<string, Stats>;
  avgMargin?: number;
  marginStdDev?: number;
  crushRate?: number;
  tightWinRate?: number;
}

export interface PropTypePredictionStats extends Stats {
  propType: string;
  hitRate: number;
  byDirection: Record<string, Stats>;
}

export interface PredictionStats {
  totalPredictions: number;
  totalResolved: number;
  lastUpdated: string;
  byTier: Record<string, Stats>;
  byTierDirection: Record<string, Stats>;
  byPropType: Record<string, PropTypePredictionStats>;
  byDirection: Record<string, Stats>;
  byPropDirection: Record<string, Stats>;
  byPlayer: Record<string, PlayerPredictionStats>;
  byLineRange: Record<string, Stats>;
  byConfidence: Record<string, Stats>;
  byEdge: Record<string, Stats>;
  byModel: Record<string, Stats>;
  byTeam: Record<string, Stats>;
  byTierPropDirection: Record<string, Stats>;
  byTierPropDirectionEdge: Record<string, Stats>;
  byTierPropDirectionConfidence: Record<string, Stats>;
  byTierPropDirectionConfidenceEdge: Record<string, Stats>;
  byHomeAway: Record<string, Stats>;
  byOpponent: Record<string, Stats>;
  byOpponentDirection: Record<string, Stats>;
  byB2BDirection: Record<string, Stats>;
  byBlowoutRiskDirection: Record<string, Stats>;
  byDefenseTierDirection: Record<string, Stats>;
  thresholds: {
    playerMinPicks: number;
    avoidHitRate: number;
    optimalHitRate: number;
  };
  playersToAvoid: string[];
  optimalPlayers: string[];
  profitablePropTypes: string[];
  unprofitablePropTypes: string[];
}

export interface StrategyStats {
  /** @deprecated Use totalSettledLegs for sample size calculations */
  totalSettled: number;
  totalSettledLegs: number;
  lastUpdated: string;
  byTierLegCount: Record<string, Stats>;
  byTierDirection: Record<string, Stats>;
  byPropCombo: Record<string, Stats>;
  byOddsRange: Record<string, Stats>;
  byLineRange: Record<string, Stats>;
  byAvgLine: Record<string, Stats>;
  byPlayer: Record<string, PlayerStats>;
  byPropType: Record<string, PropTypeStats>;
  byB2BDirection: Record<string, Stats>;
  byBlowoutRiskDirection: Record<string, Stats>;
  byDefenseTierDirection: Record<string, Stats>;
  thresholds: {
    playerMinBets: number;
    avoidHitRate: number;
    optimalHitRate: number;
    profitableProfit: number;
    unprofitableProfit: number;
  };
  playersToAvoid: string[];
  optimalPlayers: string[];
  profitablePropTypes: string[];
  unprofitablePropTypes: string[];
  assistsOverIsStrong: boolean;
}

export interface UseStrategyStatsOptions {
  httpClient: HttpClient;
  autoLoad?: boolean;
}

export interface UseStrategyStatsResult {
  stats: StrategyStats | null;
  predictionStats: PredictionStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

/** Client-side stale-time: skip fetch if data was loaded within this window */
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

/** Timestamp of last successful fetch (module-level so it persists across mounts) */
let lastFetchedAt = 0;

export function useStrategyStats(options: UseStrategyStatsOptions): UseStrategyStatsResult {
  const { httpClient, autoLoad = true } = options;
  const [stats, setStats] = useState<StrategyStats | null>(null);
  const [predictionStats, setPredictionStats] = useState<PredictionStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    // Skip fetch if data was recently loaded (stale-time check)
    if (Date.now() - lastFetchedAt < STALE_TIME_MS) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [strategyData, predictionData] = await Promise.all([
        httpClient.get<StrategyStats>('/api/lab/strategy-stats'),
        httpClient.get<PredictionStats>('/api/lab/prediction-stats').catch(() => null),
      ]);

      setStats(strategyData);
      if (predictionData) setPredictionStats(predictionData);
      lastFetchedAt = Date.now();
    } catch (err) {
      setError(ApiError.getUserMessage(err, 'Failed to load strategy stats'));
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  useEffect(() => {
    if (autoLoad) {
      fetchStats();
    }
  }, [autoLoad, fetchStats]);

  return {
    stats,
    predictionStats,
    loading,
    error,
    refresh: fetchStats,
  };
}
